/**
 * use-authed-image —— 鉴权代理图片/附件读取 hook（dev-docs A7 §2.2 口径）。
 *
 * 反馈附件 / banner 轮播图等资源经 admin JWT 门后的代理端点回传（相对路径 URL），浏览器原生
 * <img src> / <a download> 不能附带 Authorization header → 必须 authedFetch（带 Bearer + 401 自动续期，
 * 禁裸 fetch）→ blob → objectURL 喂 <img>。url 变化 / 卸载时 revoke，防 blob URL 泄漏。
 * url 传 null/undefined/空串 = 空闲态（objUrl=null, failed=false），供「本地预览优先」场景条件禁用。
 */

import { useEffect, useState } from 'react';
import { authedFetch } from '~/api/session-refresh';
import { newRequestId } from '~/lib/request-id';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export function useAuthedImage(url: string | null | undefined): {
  objUrl: string | null;
  failed: boolean;
} {
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // url 空 = 空闲态：清态且不发请求
    setObjUrl(null);
    setFailed(false);
    if (!url) return;
    let cancelled = false;
    let created: string | null = null;
    // AbortController：卸载 / url 变更时中止在飞请求（不只翻 cancelled 旗）——快速开关弹窗 / 切换 slide
    // 时不留后台继续读完整 blob 的请求（省 Worker/R2 负载与流量）
    const controller = new AbortController();
    (async () => {
      try {
        const res = await authedFetch(`${API_BASE || ''}${url}`, {
          method: 'GET',
          headers: { 'X-Request-Id': newRequestId() },
          signal: controller.signal,
        });
        if (!res.ok) {
          if (!cancelled) setFailed(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjUrl(created);
      } catch {
        // abort 引发的 reject 也落这里：cancelled 已置 true，不会误标 failed
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  return { objUrl, failed };
}
